import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/hooks/use-user";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const profileSchema = z.object({
  displayName: z.string().min(3),
  bio: z.string().min(10),
  location: z.string().min(3),
  eventPreferences: z.object({
    maxTravelDistance: z.number().min(0).max(500),
    preferredEventSize: z.enum(["small", "medium", "large", "any"]),
    priceRangeMax: z.number().min(0),
    eventType: z.enum(["clubs", "festivals", "both"]),
    preferredTimes: z.array(z.string()).min(1),
  }),
  socialPreferences: z.object({
    groupSize: z.enum(["solo", "small", "large", "any"]),
    socialLevel: z.number().min(1).max(5),
  }),
  musicPreferences: z.object({
    genres: z.array(z.string()).min(1),
    artists: z.array(z.string()),
    favoriteVenues: z.array(z.string()),
  }),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const timeSlots = [
  "morning",
  "afternoon",
  "evening",
  "night",
  "weekends",
  "weekdays",
];

export function ProfileForm() {
  const { user } = useUser();
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      displayName: user?.displayName || "",
      bio: user?.bio || "",
      location: user?.location || "",
      eventPreferences: {
        maxTravelDistance: user?.eventPreferences?.maxTravelDistance || 50,
        preferredEventSize: user?.eventPreferences?.preferredEventSize || "any",
        priceRangeMax: user?.eventPreferences?.priceRangeMax || 100,
        eventType: user?.eventPreferences?.eventType || "both",
        preferredTimes: user?.eventPreferences?.preferredTimes || ["evening", "night"],
      },
      socialPreferences: {
        groupSize: user?.socialPreferences?.groupSize || "any",
        socialLevel: user?.socialPreferences?.socialLevel || 3,
      },
      musicPreferences: {
        genres: user?.musicPreferences?.genres || [],
        artists: user?.musicPreferences?.artists || [],
        favoriteVenues: user?.musicPreferences?.favoriteVenues || [],
      },
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    try {
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Profile</CardTitle>
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? "Cancel" : "Edit"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Basic Information</h3>
                <FormField
                  control={form.control}
                  name="displayName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bio</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Event Preferences</h3>
                <FormField
                  control={form.control}
                  name="eventPreferences.maxTravelDistance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maximum Travel Distance (miles)</FormLabel>
                      <FormControl>
                        <Slider
                          min={0}
                          max={500}
                          step={10}
                          value={[field.value]}
                          onValueChange={(value) => field.onChange(value[0])}
                        />
                      </FormControl>
                      <FormDescription>
                        Current: {field.value} miles
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="eventPreferences.preferredEventSize"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Event Size</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select preferred event size" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="small">Small (under 100 people)</SelectItem>
                          <SelectItem value="medium">Medium (100-500 people)</SelectItem>
                          <SelectItem value="large">Large (500+ people)</SelectItem>
                          <SelectItem value="any">No Preference</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="eventPreferences.priceRangeMax"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maximum Price ($)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="eventPreferences.eventType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Event Type Preference</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select event type preference" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="clubs">Clubs</SelectItem>
                          <SelectItem value="festivals">Festivals</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Social Preferences</h3>
                <FormField
                  control={form.control}
                  name="socialPreferences.groupSize"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Group Size</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select preferred group size" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="solo">Solo</SelectItem>
                          <SelectItem value="small">Small Groups (2-5)</SelectItem>
                          <SelectItem value="large">Large Groups (6+)</SelectItem>
                          <SelectItem value="any">No Preference</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="socialPreferences.socialLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Social Interaction Level</FormLabel>
                      <FormControl>
                        <Slider
                          min={1}
                          max={5}
                          step={1}
                          value={[field.value]}
                          onValueChange={(value) => field.onChange(value[0])}
                        />
                      </FormControl>
                      <FormDescription>
                        {field.value === 1 && "Prefer minimal interaction"}
                        {field.value === 2 && "Casual socializing"}
                        {field.value === 3 && "Balanced"}
                        {field.value === 4 && "Very social"}
                        {field.value === 5 && "Love meeting new people"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button type="submit">Save Changes</Button>
            </form>
          </Form>
        ) : (
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium">Display Name</h3>
                <p>{user?.displayName || "Not set"}</p>
              </div>
              <div>
                <h3 className="font-medium">Bio</h3>
                <p>{user?.bio || "No bio yet"}</p>
              </div>
              <div>
                <h3 className="font-medium">Location</h3>
                <p>{user?.location || "Not set"}</p>
              </div>
            </div>

            {user?.eventPreferences && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Event Preferences</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium">Max Travel Distance</h4>
                    <p>{user.eventPreferences.maxTravelDistance} miles</p>
                  </div>
                  <div>
                    <h4 className="font-medium">Preferred Event Size</h4>
                    <p className="capitalize">{user.eventPreferences.preferredEventSize}</p>
                  </div>
                  <div>
                    <h4 className="font-medium">Maximum Price</h4>
                    <p>${user.eventPreferences.priceRangeMax}</p>
                  </div>
                  <div>
                    <h4 className="font-medium">Event Type</h4>
                    <p className="capitalize">{user.eventPreferences.eventType}</p>
                  </div>
                </div>
              </div>
            )}

            {user?.socialPreferences && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Social Preferences</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium">Preferred Group Size</h4>
                    <p className="capitalize">{user.socialPreferences.groupSize}</p>
                  </div>
                  <div>
                    <h4 className="font-medium">Social Level</h4>
                    <p>{user.socialPreferences.socialLevel}/5</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <h3 className="font-medium">Account Type</h3>
              <p className="capitalize">{user?.type}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}